import {
  ButtonHierarchy,
  ButtonSize,
  MainButton,
  Typography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Box } from "@mui/material"
import React, { useState } from "react"
import { useLocation } from "react-router-dom"
import { useAppContext, useAppDispatchContext } from "../../app/Context"
import { Colors, useCustomBranding } from "../../constants/colors"
import { getMedia, isPermissionDeniedError } from "../../lib/getMedia"
import { Agent } from "../../types/Agent"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { VideoAndMicAccessModal } from "../Modal/VideoAndMicAccess"
import { SnackbarWithText } from "../snackbar"

const lemon = "var(--primitives-desktop-primary-brand-lemon-400)"

const tosLink =
  "https://beyond-presence.notion.site/Terms-of-Use-86de0b8f3b60441bb829efa77a269e31"
const privacyLink =
  "https://beyond-presence.notion.site/Privacy-Policy-86de0b8f3b60441bb829efa77a269e31"

interface BottomProps {
  agent: Agent
  agentExists: boolean
  handleGrantPermissions: (granted: boolean) => void
}

export const Bottom: React.FC<BottomProps> = ({
  agent,
  handleGrantPermissions,
  agentExists,
}) => {
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const location = useLocation()
  const context = useAppContext()
  const dispatch = useAppDispatchContext()
  const { isEnabled: isCustomBrandingEnabled } = useCustomBranding()

  const handleAllowMicrophoneAndCamera = () => {
    requestMediaAccess()
  }

  const requestMediaAccess = async () => {
    setIsLoading(true)
    try {
      const permissions = await Promise.all([
        navigator.permissions.query({ name: "microphone" as PermissionName }),
        navigator.permissions.query({ name: "camera" as PermissionName }),
      ])
      const allGranted = permissions.every((p) => p.state === "granted")

      if (allGranted && !context.stream) {
        const stream = await getMedia(
          null,
          (error) => {
            setErrorMessage(
              isPermissionDeniedError(error)
                ? "Camera and microphone access was denied. Please enable permissions in your browser settings and try again."
                : "Unable to access camera or microphone. Please check your device settings and try again.",
            )
            setErrorOpen(true)
          },
          context,
        )
        if (stream != null) {
          dispatch({ type: StateAction.setStream, payload: stream })
          handleGrantPermissions(true)
          return
        }
      }

      setPermissionsModalOpen(true)
    } catch (error) {
      setPermissionsModalOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleModalAllowAccess = async () => {
    setIsLoading(true)
    try {
      const stream = await getMedia(
        null,
        (error) => {
          setErrorMessage(
            isPermissionDeniedError(error)
              ? "Camera and microphone access was denied. Please enable permissions in your browser settings and try again."
              : "Unable to access camera or microphone. Please check your device settings and try again.",
          )
          setErrorOpen(true)
        },
        context,
      )
      if (stream != null) {
        dispatch({ type: StateAction.setStream, payload: stream })
        setPermissionsModalOpen(false)
        handleGrantPermissions(true)
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.")
      setErrorOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoToLink = (link: string) => {
    window.open(link, "_blank")
  }

  return (
    <React.Fragment>
      <Box
        display={permissionsModalOpen ? "none" : "flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
        position={"absolute"}
        bottom={10}
        gap={"var(--spacing-tokens-size-in-px-spacing-spacing-xl)"}
      >
        <Typography size={TypographySize.HeadingXL} color={Colors.White}>
          Talk to {agent?.name || "Agent"} 👋
        </Typography>
        <MainButton
          onClick={handleAllowMicrophoneAndCamera}
          hierarchy={ButtonHierarchy.PrimaryLemon}
          size={ButtonSize.md}
          text={agentExists ? "Start Conversation" : "Agent Not Found"}
          disabled={!agentExists || isLoading}
        />
        <Box padding="var(--spacing-tokens-size-in-px-spacing-spacing-lg) var(--spacing-tokens-size-in-px-spacing-spacing-xl)">
          {!isCustomBrandingEnabled && (
            <Typography
              size={TypographySize.TextS}
              weight={TypographyWeight.Medium}
              color="var(--colors-light-text-text-white)"
            >
              By clicking on "Start Conversation" you agree to our{" "}
              <span
                style={{ color: lemon, cursor: "pointer" }}
                onClick={() => handleGoToLink(tosLink)}
              >
                Terms of Service
              </span>{" "}
              and{" "}
              <span
                style={{ color: lemon, cursor: "pointer" }}
                onClick={() => handleGoToLink(privacyLink)}
              >
                Privacy Policy
              </span>
              .
            </Typography>
          )}
        </Box>
      </Box>
      <VideoAndMicAccessModal
        open={permissionsModalOpen}
        onRequestAccess={handleModalAllowAccess}
        isLoading={isLoading}
      />
      <SnackbarWithText
        open={errorOpen}
        text={errorMessage}
        severity={SnackbarType.error}
        handleClose={() => {
          setErrorOpen(false)
          setErrorMessage("")
        }}
      />
    </React.Fragment>
  )
}
